/**
 * useNetworkStatus Hook
 * 
 * Detects and monitors network connectivity status
 * Validates: Requirements 18.5
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Network status information
 */
export interface NetworkStatus {
  /**
   * Whether the device is online
   */
  isOnline: boolean;
  
  /**
   * Whether the backend is reachable
   */
  isBackendReachable: boolean;
  
  /**
   * Last time the backend was checked
   */
  lastChecked?: Date;
  
  /**
   * Whether a check is in progress
   */
  isChecking: boolean;
}

/**
 * Options for network status hook
 */
export interface UseNetworkStatusOptions {
  /**
   * Whether to automatically check backend connectivity
   */
  checkBackend?: boolean;
  
  /**
   * Interval for backend checks (ms)
   */
  checkInterval?: number;
  
  /**
   * Backend health check URL
   */
  healthCheckUrl?: string;
  
  /**
   * Callback when status changes
   */
  onStatusChange?: (status: NetworkStatus) => void;
}

/**
 * Hook to monitor network connectivity
 */
export function useNetworkStatus(options: UseNetworkStatusOptions = {}) {
  const {
    checkBackend = true,
    checkInterval = 30000, // 30 seconds
    healthCheckUrl = '/api/health',
    onStatusChange,
  } = options;

  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isBackendReachable: true, // Assume true initially
    isChecking: false,
  });

  /**
   * Check if backend is reachable
   */
  const checkBackendConnectivity = useCallback(async (): Promise<boolean> => {
    if (!navigator.onLine) {
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(healthCheckUrl, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache',
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.warn('Backend connectivity check failed:', error);
      return false;
    }
  }, [healthCheckUrl]);

  /**
   * Update network status
   */
  const updateStatus = useCallback(async () => {
    setStatus(prev => ({ ...prev, isChecking: true }));

    const isOnline = navigator.onLine;
    const isBackendReachable = checkBackend && isOnline
      ? await checkBackendConnectivity()
      : isOnline;

    const newStatus: NetworkStatus = {
      isOnline,
      isBackendReachable,
      lastChecked: new Date(),
      isChecking: false,
    };

    setStatus(newStatus);

    // Notify if status changed
    if (onStatusChange) {
      onStatusChange(newStatus);
    }
  }, [checkBackend, checkBackendConnectivity, onStatusChange]);

  /**
   * Manual refresh of network status
   */
  const refresh = useCallback(() => {
    updateStatus();
  }, [updateStatus]);

  // Listen to online/offline events
  useEffect(() => {
    const handleOnline = () => {
      updateStatus();
    };

    const handleOffline = () => {
      setStatus(prev => ({
        ...prev,
        isOnline: false,
        isBackendReachable: false,
        lastChecked: new Date(),
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [updateStatus]);

  // Periodic backend connectivity check
  useEffect(() => {
    if (!checkBackend || checkInterval <= 0) {
      return;
    }

    // Initial check
    updateStatus();

    // Periodic checks
    const intervalId = setInterval(updateStatus, checkInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [checkBackend, checkInterval, updateStatus]);

  return {
    ...status,
    refresh,
  };
}
